/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { task } from 'ember-concurrency';
import { formatNumber } from 'cio/helpers/format-number';

import EnvironmentService from 'cio/services/environment';
import AlertsService from 'cio/services/alerts';
import ObjectTypeModel from 'cio/models/object-type';
import { pluralize } from 'cio/helpers/pluralize';
import { capitalize } from '@ember/string';
import ObjectTypesService from 'cio/services/object-types';

interface ObjectsDeleteConfirmModalArgs {
  deletedCount: number;
  isBulkDeletion: boolean;
  isDisabled: boolean;
  onClose: () => unknown;
  confirmAction: () => unknown;
  objectType: ObjectTypeModel;
}

const DEFAULT_VALUES = {
  confirmationString: '',
};

export default class ObjectsDeleteConfirmModal extends Component<ObjectsDeleteConfirmModalArgs> {
  @service environment!: EnvironmentService;
  @service alerts!: AlertsService;
  @service('objectTypes') objectTypesService!: ObjectTypesService;

  @tracked confirmationString = DEFAULT_VALUES.confirmationString;

  get name() {
    return pluralize([this.args.deletedCount, this.args.objectType.name]);
  }

  get isConfirmSubmitDisabled() {
    let validConfirmationString = formatNumber([this.args.deletedCount]);
    let hasTypedValidConfirmationString = !(
      this.confirmationString === validConfirmationString ||
      this.confirmationString === this.args.deletedCount.toString()
    );

    return (
      this.confirmTask.isRunning || hasTypedValidConfirmationString || !this.objectTypesService.canConfigureObjects
    );
  }

  closeAndResetState() {
    this.confirmationString = DEFAULT_VALUES.confirmationString;
    this.args.onClose();
  }

  confirmTask = task(async () => {
    try {
      await this.args.confirmAction();
      let alertText = `The selected items will be deleted soon`;
      this.alerts.success(`${this.name}.delete`, alertText);
      this.closeAndResetState();
    } catch (e) {
      this.alerts.error(
        `${this.name}.delete.modal`,
        `Something went wrong with deleting ${this.name}. ${
          e.errors[0].detail != null && capitalize(e.errors[0].detail)
        }. Please try again or contact support.`,
      );
    }
  });

  @action
  closeModal() {
    if (!this.confirmTask.isRunning) {
      this.closeAndResetState();
    }
  }
}
